import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Building2,
  Calendar,
  Loader2,
  AlertTriangle,
  User
} from 'lucide-react';
import PageContainer from '@/components/common/PageContainer';

interface Vendor {
  id: string;
  name: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

const VendorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { toast } = useToast();
  
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchVendor();
    }
  }, [id]);

  const fetchVendor = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      setVendor(data);
    } catch (error) {
      console.error('Erro ao buscar fornecedor:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados do fornecedor.',
        variant: 'destructive'
      });
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/vendors/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!vendor) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', vendor.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Fornecedor excluído com sucesso!',
        variant: 'default'
      });

      navigate('/vendors');
    } catch (error) {
      console.error('Erro ao excluir fornecedor:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o fornecedor.',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  if (!vendor) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-extrabold">Fornecedor não encontrado</h3>
            <p className="text-muted-foreground mt-2">
              O fornecedor que você está procurando não existe ou foi removido.
            </p>
            <Button className="mt-4" onClick={() => navigate('/vendors')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Fornecedores
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/vendors')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-extrabold">{vendor.name}</h1>
              <p className="text-muted-foreground">
                Detalhes do fornecedor
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informações Básicas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-extrabold text-lg">
                    {getInitials(vendor.name)}
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold">{vendor.name}</h2>
                    <Badge variant={vendor.status === 'active' ? 'default' : 'secondary'}>
                      {vendor.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Information */}
            <Card>
              <CardHeader>
                <CardTitle>Informações Adicionais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-extrabold mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Criado em
                    </h3>
                    <p className="text-muted-foreground">
                      {vendor.created_at ? formatDate(vendor.created_at) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-extrabold mb-2">Última atualização</h3>
                    <p className="text-muted-foreground">
                      {vendor.updated_at ? formatDate(vendor.updated_at) : 'N/A'}
                    </p>
                  </div>
                </div>

                {vendor.created_by && (
                  <div>
                    <h3 className="font-extrabold mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Criado por
                    </h3>
                    <p className="text-muted-foreground">
                      {vendor.created_by}
                    </p>
                  </div>
                )}

                {vendor.updated_by && (
                  <div>
                    <h3 className="font-extrabold mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Atualizado por
                    </h3>
                    <p className="text-muted-foreground">
                      {vendor.updated_by}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge 
                  variant={vendor.status === 'active' ? 'default' : 'secondary'}
                  className="text-sm"
                >
                  {vendor.status === 'active' ? 'Ativo' : 'Inativo'}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleEdit}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Fornecedor
                </Button>
                <Button 
                  variant="destructive" 
                  className="w-full justify-start"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Fornecedor
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Exclusão</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir o fornecedor "{vendor.name}"? 
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
};

export default VendorDetail;
